import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { verifyOtp } from '../../redux/actions/verifyOtpAction';
import { resetVerifyOtpState } from '../../redux/slices/verifyOtpSlice';
import { sendResetCode } from '../../redux/actions/sendResetCodeAction';
import { resetSendResetCodeState } from '../../redux/slices/sendResetCodeSlice';
import { useDarkMode } from '../../App';
import { motion } from 'framer-motion';
import { FiCheckCircle, FiMail, FiRefreshCw, FiArrowLeft, FiSmartphone, FiGlobe } from 'react-icons/fi';
import '../AuthShared.css';

const LanguageSwitcher = () => {
  const { i18n } = useTranslation('global');
  const isAR = i18n.language === 'ar';

  const toggleLanguage = () => {
    const newLang = isAR ? 'en' : 'ar';
    i18n.changeLanguage(newLang);
    localStorage.setItem('lang', newLang);
  };

  return (
    <motion.button
      className="auth-lang-switcher"
      onClick={toggleLanguage}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      <FiGlobe />
      <span>{isAR ? 'English' : 'العربية'}</span>
    </motion.button>
  );
};


const VerifyOTP = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // إذا لم يوجد إيميل، أعد التوجيه لصفحة التسجيل
  useEffect(() => {
    if (!location.state?.email) {
      navigate('/createaccount');
    }
  }, [location.state, navigate]);
  const { t, i18n } = useTranslation('global');
  const { isDarkMode } = useDarkMode();
  const dispatch = useDispatch();
  const verifyState = useSelector((state) => state.verifyOtp);
  const { isLoading, success, error, data } = verifyState || {};
  const sendResetState = useSelector((state) => state.sendResetCode);

  const [email, setEmail] = useState(location.state?.email || '');
  const [code, setCode] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = t('verify.title', 'Verify Account');
  }, [t]);

  useEffect(() => {
    if (success) {
      toast.success(data?.message || t('verify.successMessage', 'Account verified successfully!'));
      setTimeout(() => navigate('/login'), 1000);
    }
  }, [success, data, t, navigate]);

  useEffect(() => {
    if (sendResetState?.success) {
      toast.success(t('verify.codeSent', 'Verification code sent successfully!'));
      dispatch(resetSendResetCodeState());
    }
  }, [sendResetState?.success, t, dispatch]);

  useEffect(() => {
    if (error) {
      toast(error);
      dispatch(resetVerifyOtpState());
    }
  }, [error, dispatch]);

  useEffect(() => () => {
    dispatch(resetVerifyOtpState());
  }, [dispatch]);

  const handleCodeInput = (value) => {
    const sanitized = value.replace(/\D/g, '').slice(0, 6);
    setCode(sanitized);
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    if (!/^\d+$/.test(pastedData)) return;
    const sanitized = pastedData.replace(/\D/g, '').slice(0, 6);
    setCode(sanitized);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (!email) {
      toast(t('verify.emailRequired', 'Email is required'));
      return;
    }
    if (code.length !== 6) {
      toast(t('verify.enterDigits', 'Please enter all 6 digits'));
      return;
    }
    dispatch(verifyOtp({ email, code, lang: i18n.language }));
  };

  const handleResendCode = () => {
    if (!email) {
      toast(t('verify.emailRequired', 'Email is required'));
      return;
    }
    dispatch(sendResetCode({ email, lang: i18n.language }));
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.6, staggerChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.5 } }
  };

  return (
    <div className={`auth-page ${isDarkMode ? 'dark' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="auth-bg-overlay"></div>
      <div className="auth-blobs">
        <div className="auth-blob"></div>
        <div className="auth-blob"></div>
        <div className="auth-blob"></div>
      </div>

      <LanguageSwitcher />

      <motion.div 
        className="auth-container"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <motion.div className="auth-card" variants={itemVariants}>
          <div className="auth-brand">
            <motion.div className="auth-brand-circle" whileHover={{ scale: 1.05, rotate: 5 }}>
              <FiCheckCircle style={{ fontSize: '24px', color: '#fff' }} />
            </motion.div>
            <h1>{t('verify.brand', 'OnePlus Security')}</h1>
          </div>

          <div className="auth-header">
            <h2>{t('verify.title', 'Verification')}</h2>
            <p>{t('verify.subtitle', 'Enter the 6-digit code sent to your email')}</p>
          </div>

          <form className="auth-form" onSubmit={handleSubmit}>
            <div className="auth-input-group">
              <label><FiMail /> {t('verify.email', 'Email Address')}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('verify.emailPlaceholder', 'example@mail.com')}
                className={submitted && !email ? 'error' : ''}
              />
            </div>

            <div className="auth-input-group">
              <label><FiSmartphone /> {t('verify.code', 'Verification Code')}</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength="6"
                value={code}
                onChange={(e) => handleCodeInput(e.target.value)}
                onPaste={handlePaste}
                placeholder={t('verify.codePlaceholder', '000000')}
                style={{ textAlign: 'center', letterSpacing: '8px', fontSize: '20px', fontWeight: 'bold' }}
                className={submitted && code.length < 6 ? 'error' : ''}
              />
            </div>

            <motion.button 
              type="submit" 
              className="auth-submit-btn" 
              disabled={isLoading}
              whileTap={{ scale: 0.98 }}
            >
              {isLoading ? <span className="auth-spinner"></span> : t('verify.verify', 'Verify Account')}
            </motion.button>

            <div className="auth-footer" style={{ marginTop: '10px' }}>
              <p>{t('verify.didntReceive', "Didn't receive code?")}</p>
              <button
                type="button"
                className="auth-link"
                style={{ background: 'none', border: 'none', padding: 0 }}
                onClick={handleResendCode}
                disabled={sendResetState?.isLoading}
              >
                {sendResetState?.isLoading ? t('verify.resending', 'Sending...') : (
                  <>
                    <FiRefreshCw style={{ verticalAlign: 'middle', marginRight: '5px' }} />
                    {t('verify.resendCode', 'Resend Code')}
                  </>
                )}
              </button>
            </div>
          </form>

          <div className="auth-footer">
            <button className="auth-back-btn" onClick={() => navigate('/createaccount')}>
              <FiArrowLeft style={{ verticalAlign: 'middle', marginRight: '5px' }} />
              {t('verify.backToRegister', 'Back to Register')}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default VerifyOTP;

